import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";

// Reusing the existing Privacy Policy stylesheet as requested.
import "./privacypolicy.css";
import SEO from "../../components/seo/SEO";

const TermsConditions = () => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  return (
    <>
      <SEO
        title="Terms & Conditions | No Wasted Days"
        description="Terms and Conditions of Use for the No Wasted Days application."
        canonicalUrl={"https://nowasteddays.io/terms-and-conditions"}
      />

      {userInfo ? <Header variant="light" /> : <Header variant="light" />}

      <main className="privacy-policy-container">
        <div className="privacy-policy-tile">
          <h1>Terms and Conditions</h1>
          <p className="last-updated">Last Updated: 17th August 2025</p>
          <p>
            These terms and conditions ("Terms") govern your use of the web
            application NoWastedDays.io ("the App"). By accessing or using the
            App, you agree to be bound by these Terms. If you do not agree to
            these Terms, you may not use the App.
          </p>

          <section>
            <h2>1. Account Registration</h2>
            <p>
              <strong>1.1.</strong> To use the App, you must create an account
              by providing accurate and complete information, including your
              name, email address, and password. You agree to keep your account
              information up to date.
            </p>
            <p>
              <strong>1.2.</strong> You are responsible for maintaining the
              security and confidentiality of your account credentials. Any
              activity that occurs under your account is your sole
              responsibility. You must notify us immediately of any unauthorized
              use of your account.
            </p>
          </section>

          <section>
            <h2>2. User Content</h2>
            <p>
              <strong>2.1.</strong> The App allows you to input and store
              personal information, including but not limited to your Diary
              Date, Gratitude Statement, Detachment Statement, Assurance
              Statement, Moment Scores, Remember Today Log, Leave Behind Log,
              Start and End Scores, Vision, Values, Date of Birth, Ikigai, and
              Quotes ("User Content").
            </p>
            <p>
              <strong>2.2.</strong> You retain all ownership rights in your User
              Content. By using the App, you grant us a worldwide,
              non-exclusive, royalty-free license to use, reproduce, modify,
              adapt, publish, translate, distribute, and display your User
              Content solely for the purpose of providing you with the services
              of the App.
            </p>
            <p>
              <strong>2.3.</strong> You represent and warrant that you have the
              necessary rights to grant the license in section 2.2 and that your
              User Content does not infringe or violate any third party's rights
              or any applicable laws.
            </p>
            <p>
              <strong>2.4.</strong> We reserve the right, but not the
              obligation, to review and remove any User Content that violates
              these Terms or is otherwise objectionable.
            </p>
          </section>

          <section>
            <h2>3. Data Privacy</h2>
            <p>
              <strong>3.1.</strong> We are committed to protecting your privacy
              and handling your personal information in accordance with
              applicable data protection laws. Please review our{" "}
              <Link to="/privacy-policy" className="inline-link">
                Privacy Policy
              </Link>{" "}
              to understand how we collect, use, and disclose your personal
              information.
            </p>
          </section>

          <section>
            <h2>4. Intellectual Property Rights</h2>
            <p>
              <strong>4.1.</strong> The App and its contents, including but not
              limited to text, graphics, logos, images, software, and design
              elements, are owned by or licensed to us and are protected by
              intellectual property laws. You may not copy, modify, distribute,
              display, license, or sell any content obtained from the App
              without our prior written permission.
            </p>
          </section>

          <section>
            <h2>5. Disclaimer of Warranties</h2>
            <p>
              <strong>5.1.</strong> The App is provided on an "as is" and "as
              available" basis, without any warranties or representations of any
              kind, whether express, implied, or statutory. We do not warrant
              that the App will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section>
            <h2>6. Limitation of Liability</h2>
            <p>
              <strong>6.1.</strong> To the maximum extent permitted by law, we
              shall not be liable for any indirect, incidental, consequential,
              special, punitive, or exemplary damages, including but not limited
              to loss of profits, data, or goodwill, arising out of or in
              connection with the use of the App.
            </p>
          </section>

          <section>
            <h2>7. Termination</h2>
            <p>
              <strong>7.1.</strong> We may terminate or suspend your access to
              the App without prior notice or liability if you breach these
              Terms.
            </p>
          </section>

          <section>
            <h2>8. Modifications to the Terms</h2>
            <p>
              <strong>8.1.</strong> We reserve the right to modify or update
              these Terms at any time. We will notify you of any significant
              changes to these Terms by sending an email to the address
              associated with your account. Your continued use of the App after
              the changes have been made will constitute your acceptance of the
              revised Terms.
            </p>
          </section>

          <section>
            <h2>9. Governing Law and Jurisdiction</h2>
            <p>
              <strong>9.1.</strong> These Terms shall be governed by and
              construed in accordance with the laws of United Kingdom. Any
              disputes arising out of or in connection with these Terms shall be
              subject to the exclusive jurisdiction of the courts of United
              Kingdom.
            </p>
          </section>

          <section>
            <h2>10. Competitions & The NoWastedDays Challenge</h2>
            <p>
              <strong>10.1.</strong> We may reserve the right to not pay the £10
              incentive if it is not completely clear that the criteria has been
              honestly and fairly completed, or if the number of entrants exceed
              our initial targeted amount.
            </p>
          </section>
        </div>
      </main>

      <TestFooter />
    </>
  );
};

export default TermsConditions;
